// src/components/admin/AdminJobsSection.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import toast from "react-hot-toast";
import { ClipboardList, RotateCw, Activity, Clock } from "lucide-react";

const AdminJobsSection = () => {
  // -------- Proforma sync state --------
  const [fetchDetails, setFetchDetails] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [maxItems, setMaxItems] = useState(0);

  const [isSyncing, setIsSyncing] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskState, setTaskState] = useState(null);
  const [summary, setSummary] = useState(null);
  const [, setHistory] = useState([]);

  // -------- PreCottage fetch state --------
  const [pcPageSize, setPcPageSize] = useState(500);
  const [pcStartIndex, setPcStartIndex] = useState(0);
  const [pcDoSave, setPcDoSave] = useState(true);
  const [pcDebug, setPcDebug] = useState(false);
  const [pcEchoUpstream, setPcEchoUpstream] = useState(false);
  const [pcMaxPages, setPcMaxPages] = useState(200);

  const [pcIsRunning, setPcIsRunning] = useState(false);
  const [pcTaskId, setPcTaskId] = useState(null);
  const [pcTaskState, setPcTaskState] = useState(null);
  const [pcSummary, setPcSummary] = useState(null);
  const [pcHistory, setPcHistory] = useState([]);

  // -------- Cottage import state --------
  const [ciPageSize, setCiPageSize] = useState(500);
  const [ciStartIndex, setCiStartIndex] = useState(0);
  const [ciMaxPages, setCiMaxPages] = useState(200);

  const [ciIsRunning, setCiIsRunning] = useState(false);
  const [ciTaskId, setCiTaskId] = useState(null);
  const [ciTaskState, setCiTaskState] = useState(null);
  const [ciSummary, setCiSummary] = useState(null);
  const [ciHistory, setCiHistory] = useState([]);

  // -------- Full NTSW sync state --------
  const [fullIsRunning, setFullIsRunning] = useState(false);
  const [fullTaskId, setFullTaskId] = useState(null);
  const [fullTaskState, setFullTaskState] = useState(null);
  const [fullProgress, setFullProgress] = useState(null);
  const [fullResult, setFullResult] = useState(null);

  // -------- Full NTSW sync polling --------
  useEffect(() => {
    if (!fullTaskId) return;

    setFullTaskState("PENDING");
    let cancelled = false;

    const pollFullStatus = async () => {
      try {
        const res = await axiosInstance.get(`/ntsw/full-sync/status/${fullTaskId}/`);
        if (cancelled) return;

        const state = res.data.state;
        setFullTaskState(state);

        if (state === "PROGRESS") {
          setFullProgress(res.data.progress || null);
        } else if (state === "SUCCESS") {
          const result = res.data.result || {};
          setFullResult(result);
          setFullProgress(result);
          setFullIsRunning(false);
          if (result.ok === false) {
            toast.error(result.error || "همگام‌سازی کامل با خطا تمام شد.");
          } else {
            toast.success("همگام‌سازی کامل سامانه جامع با موفقیت تمام شد.");
          }
          cancelled = true;
        } else if (state === "FAILURE" || state === "REVOKED") {
          setFullIsRunning(false);
          toast.error(res.data.error || "خطا در همگام‌سازی کامل سامانه جامع");
          cancelled = true;
        }
      } catch (err) {
        if (!cancelled) {
          setFullIsRunning(false);
          toast.error("خطا در دریافت وضعیت همگام‌سازی کامل");
          cancelled = true;
        }
      }

      if (!cancelled) {
        setTimeout(pollFullStatus, 3000);
      }
    };

    pollFullStatus();

    return () => {
      cancelled = true;
    };
  }, [fullTaskId]);

  // -------- Proforma polling --------
  useEffect(() => {
    if (!taskId) return;

    setTaskState("PENDING");
    let cancelled = false;

    const pollStatus = async () => {
      try {
        const res = await axiosInstance.get(`/proforma-sync/status/${taskId}/`);
        if (cancelled) return;

        const state = res.data.state;
        setTaskState(state);

        if (state === "SUCCESS") {
          const result = res.data.result || {};
          const s = result.summary || result;
          setSummary(s);
          setIsSyncing(false);
          if (result.ok === false) {
            toast.error(result.error || "همگام‌سازی پروفرماها ناموفق بود.");
          } else {
            toast.success("همگام‌سازی پروفرماها با موفقیت انجام شد.");
          }

          setHistory((prev) => [
            {
              id: taskId,
              state,
              summary: s,
              finishedAt: new Date().toISOString(),
            },
            ...prev,
          ]);

          cancelled = true;
        } else if (state === "FAILURE" || state === "REVOKED") {
          setIsSyncing(false);
          const errMsg =
            res.data.error ||
            (typeof res.data.result === "string"
              ? res.data.result
              : "خطا در اجرای همگام‌سازی");
          toast.error(errMsg);
          cancelled = true;
        }
      } catch (err) {
        if (!cancelled) {
          setIsSyncing(false);
          toast.error("خطا در دریافت وضعیت همگام‌سازی");
          cancelled = true;
        }
      }

      if (!cancelled) {
        setTimeout(pollStatus, 3000);
      }
    };

    pollStatus();

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  // -------- PreCottage polling --------
  useEffect(() => {
    if (!pcTaskId) return;

    setPcTaskState("PENDING");
    let cancelled = false;

    const pollPcStatus = async () => {
      try {
        const res = await axiosInstance.get(
          `/precottage/fetch-job/status/${pcTaskId}/`
        );
        if (cancelled) return;

        const state = res.data.state;
        setPcTaskState(state);

        if (state === "SUCCESS") {
          const result = res.data.result || {};
          const s = result.summary || result;
          setPcSummary(s);
          setPcIsRunning(false);
          toast.success("دریافت پیش‌کوتاژها با موفقیت انجام شد.");

          setPcHistory((prev) => [
            {
              id: pcTaskId,
              state,
              summary: s,
              finishedAt: new Date().toISOString(),
            },
            ...prev,
          ]);

          cancelled = true;
        } else if (state === "FAILURE" || state === "REVOKED") {
          setPcIsRunning(false);
          const errMsg =
            res.data.error ||
            (typeof res.data.result === "string"
              ? res.data.result
              : "خطا در اجرای دریافت پیش‌کوتاژها");
          toast.error(errMsg);
          cancelled = true;
        }
      } catch (err) {
        if (!cancelled) {
          setPcIsRunning(false);
          toast.error("خطا در دریافت وضعیت دریافت پیش‌کوتاژها");
          cancelled = true;
        }
      }

      if (!cancelled) {
        setTimeout(pollPcStatus, 3000);
      }
    };

    pollPcStatus();

    return () => {
      cancelled = true;
    };
  }, [pcTaskId]);

  // -------- Cottage import polling --------
  useEffect(() => {
    if (!ciTaskId) return;

    setCiTaskState("PENDING");
    let cancelled = false;

    const pollCiStatus = async () => {
      try {
        const res = await axiosInstance.get(
          `/cottage/import-job/status/${ciTaskId}/`
        );
        if (cancelled) return;

        const state = res.data.state;
        setCiTaskState(state);

        if (state === "SUCCESS") {
          const result = res.data.result || {};
          const s = result.summary || result;
          setCiSummary(s);
          setCiIsRunning(false);
          toast.success("ایمپورت کوتاژها با موفقیت انجام شد.");

          setCiHistory((prev) => [
            {
              id: ciTaskId,
              state,
              summary: s,
              finishedAt: new Date().toISOString(),
            },
            ...prev,
          ]);

          cancelled = true;
        } else if (state === "FAILURE" || state === "REVOKED") {
          setCiIsRunning(false);
          const errMsg =
            res.data.error ||
            (typeof res.data.result === "string"
              ? res.data.result
              : "خطا در اجرای ایمپورت کوتاژها");
          toast.error(errMsg);
          cancelled = true;
        }
      } catch (err) {
        if (!cancelled) {
          setCiIsRunning(false);
          toast.error("خطا در دریافت وضعیت ایمپورت کوتاژها");
          cancelled = true;
        }
      }

      if (!cancelled) {
        setTimeout(pollCiStatus, 3000);
      }
    };

    pollCiStatus();

    return () => {
      cancelled = true;
    };
  }, [ciTaskId]);

  // -------- start handlers --------
  const startFullSync = async () => {
    setFullProgress(null);
    setFullResult(null);
    setFullTaskState(null);

    const loadingToast = toast.loading("در حال شروع همگام‌سازی کامل سامانه جامع...");
    setFullIsRunning(true);

    try {
      const res = await axiosInstance.post("/ntsw/full-sync/", {
        proforma_fetch_details: true,
        proforma_dry_run: false,
        proforma_max_items: 0,
        proforma_page_size: 2000,
        proforma_max_pages: 50,
        precottage_page_size: 500,
        precottage_start_index: 0,
        precottage_max_pages: 200,
        precottage_do_save: true,
        cottage_page_size: 500,
        cottage_start_index: 0,
        cottage_max_pages: 200,
        relink_precottages_after_cottages: true,
        stop_on_error: true,
      });
      const newTaskId = res.data.task_id;
      setFullTaskId(newTaskId || null);
      setFullTaskState(res.data.status || "PENDING");
      toast.success("همگام‌سازی کامل در صف پردازش قرار گرفت.", {
        id: loadingToast,
      });

      if (!newTaskId) {
        setFullIsRunning(false);
        toast.error("شناسه تسک همگام‌سازی کامل از سرور دریافت نشد.");
      }
    } catch (err) {
      setFullIsRunning(false);
      toast.error("شروع همگام‌سازی کامل ناموفق بود.", {
        id: loadingToast,
      });
    }
  };

  const startSync = async () => {
    setSummary(null);
    setTaskState(null);

    const loadingToast = toast.loading("در حال شروع همگام‌سازی پروفرماها...");
    setIsSyncing(true);

    try {
      const payload = {
        fetch_details: fetchDetails,
        dry_run: dryRun,
        max_items: Number(maxItems) || 0,
      };

      const res = await axiosInstance.post("/proforma-sync/", payload);
      const newTaskId = res.data.task_id;
      setTaskId(newTaskId || null);
      setTaskState(res.data.status || "PENDING");

      toast.success("همگام‌سازی در صف پردازش قرار گرفت.", {
        id: loadingToast,
      });

      if (!newTaskId) {
        setIsSyncing(false);
        toast.error("شناسه‌ی تسک از سرور دریافت نشد.");
      }
    } catch (err) {
      setIsSyncing(false);
      toast.error("شروع همگام‌سازی پروفرماها ناموفق بود.", {
        id: loadingToast,
      });
    }
  };

  const startPreCottageFetch = async () => {
    setPcSummary(null);
    setPcTaskState(null);

    const loadingToast = toast.loading("در حال شروع دریافت پیش‌کوتاژها...");
    setPcIsRunning(true);

    try {
      const payload = {
        page_size: Number(pcPageSize) || 50,
        start_index: Number(pcStartIndex) || 0,
        do_save: pcDoSave,
        debug: pcDebug,
        echo_upstream: pcEchoUpstream,
        echo_last_only: true,
        max_pages: Number(pcMaxPages) || 3,
        max_excerpt_chars: 800,
      };

      const res = await axiosInstance.post("/precottage/fetch-job/", payload);
      const newTaskId = res.data.task_id;
      setPcTaskId(newTaskId || null);
      setPcTaskState(res.data.status || "PENDING");

      toast.success("دریافت پیش‌کوتاژها در صف پردازش قرار گرفت.", {
        id: loadingToast,
      });

      if (!newTaskId) {
        setPcIsRunning(false);
        toast.error("شناسه تسک دریافت پیش‌کوتاژ از سرور دریافت نشد.");
      }
    } catch (err) {
      setPcIsRunning(false);
      toast.error("شروع دریافت پیش‌کوتاژها ناموفق بود.", {
        id: loadingToast,
      });
    }
  };

  const startCottageImport = async () => {
    setCiSummary(null);
    setCiTaskState(null);

    const loadingToast = toast.loading("در حال شروع ایمپورت کوتاژها...");
    setCiIsRunning(true);

    try {
      const payload = {
        PageSize: Number(ciPageSize) || 500,
        StartIndex: Number(ciStartIndex) || 0,
        MaxPages: Number(ciMaxPages) || 200,
      };

      const res = await axiosInstance.post("/cottage/import-job/", payload);
      const newTaskId = res.data.task_id;
      setCiTaskId(newTaskId || null);
      setCiTaskState(res.data.status || "PENDING");

      toast.success("ایمپورت کوتاژها در صف پردازش قرار گرفت.", {
        id: loadingToast,
      });

      if (!newTaskId) {
        setCiIsRunning(false);
        toast.error("شناسه تسک ایمپورت کوتاژ از سرور دریافت نشد.");
      }
    } catch (err) {
      setCiIsRunning(false);
      toast.error("شروع ایمپورت کوتاژها ناموفق بود.", {
        id: loadingToast,
      });
    }
  };

  const formatNumber = (v) =>
    typeof v === "number" ? v.toLocaleString("fa-IR") : v ?? "-";

  return (
    <div
      className="max-w-6xl mx-auto grid grid-cols-1 xl:grid-cols-2 gap-6"
      dir="rtl"
    >
      <div className="xl:col-span-2 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-7 space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white shadow-sm">
              <RotateCw size={21} className={fullIsRunning ? "animate-spin" : ""} />
            </div>
            <div className="flex flex-col items-end">
              <h2 className="text-base sm:text-lg font-bold text-slate-900">
                همگام‌سازی کامل سامانه جامع
              </h2>
              <p className="text-[11px] sm:text-xs text-slate-500">
                ورود به NTSW، دریافت ثبت سفارش‌ها، پیش‌کوتاژها، کوتاژها و اتصال دوباره پیش‌کوتاژها
              </p>
            </div>
          </div>

          <button
            onClick={startFullSync}
            disabled={fullIsRunning}
            className={`px-4 sm:px-5 py-2.5 inline-flex items-center justify-center gap-2 rounded-xl text-xs sm:text-sm font-semibold text-white shadow-md transition ${
              fullIsRunning
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-l from-emerald-500 via-teal-500 to-blue-600 hover:from-emerald-600 hover:via-teal-600 hover:to-blue-700"
            }`}
          >
            <RotateCw size={18} className={fullIsRunning ? "animate-spin" : ""} />
            {fullIsRunning ? "در حال همگام‌سازی کامل..." : "شروع همگام‌سازی کامل"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs text-slate-700">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span>شناسه تسک:</span>
              <code className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 truncate">
                {fullTaskId || "-"}
              </code>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>وضعیت:</span>
              <span
                className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                  fullTaskState === "SUCCESS"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : fullTaskState === "FAILURE" || fullTaskState === "REVOKED"
                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
              >
                {fullTaskState || "بدون اجرا"}
              </span>
            </div>
            {fullProgress?.current_step && (
              <div className="flex items-center justify-between gap-2">
                <span>مرحله فعلی:</span>
                <span className="font-semibold text-slate-900">
                  {fullProgress.current_step}
                </span>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <h3 className="mb-2 text-xs font-semibold text-slate-800">
              مراحل اجرا
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                ["ntsw_login", "ورود NTSW"],
                ["orders", "ثبت سفارش‌ها"],
                ["currency_allocation_statuses", "تخصیص‌های ارز و وضعیت آن‌ها"],
                ["pre_cottages", "پیش‌کوتاژها"],
                ["cottages", "کوتاژها"],
                ["exported_cottages", "کوتاژهای صادراتی"],
                ["pre_cottages_relink", "اتصال دوباره"],
              ].map(([key, label]) => {
                const step = fullProgress?.steps?.[key] || fullResult?.steps?.[key];
                const state = step?.state || "PENDING";
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-2 rounded-xl bg-white border border-slate-200 px-3 py-2"
                  >
                    <span>{label}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        state === "SUCCESS"
                          ? "bg-emerald-50 text-emerald-700"
                          : state === "FAILED"
                          ? "bg-rose-50 text-rose-700"
                          : state === "RUNNING"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {state}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* کارت ۱: همگام‌سازی پروفرماها */}
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-7 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white shadow-sm">
            <ClipboardList size={20} />
          </div>
          <div className="flex flex-col items-end">
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              همگام‌سازی پروفرماها از سامانه جامع
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500">
              خواندن پروفرماهای سامانه و تطبیق با سیستم اوژن
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
          اطلاعات دسترسی و کوکی‌های سامانه جامع از تنظیمات قبلی خوانده می‌شود.
          می‌توانید اجرای آزمایشی (بدون ذخیره) یا محدودکردن تعداد پروفرماها را
          از این بخش تنظیم کنید.
        </p>

        <div className="space-y-4 text-xs sm:text-sm">
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={fetchDetails}
                onChange={(e) => setFetchDetails(e.target.checked)}
                className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-sky-500"
              />
              دریافت جزئیات بانکی هر پروفرما (GetRegedOrderDetails)
            </label>
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              اجرای آزمایشی (بدون ذخیره در دیتابیس)
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              حداکثر تعداد آیتم برای همگام‌سازی
            </label>
            <input
              type="number"
              min="0"
              value={maxItems}
              onChange={(e) => setMaxItems(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
              placeholder="0 = بدون محدودیت"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={startSync}
            disabled={isSyncing}
            className={`px-4 sm:px-5 py-2.5 inline-flex items-center gap-2 rounded-xl text-xs sm:text-sm font-semibold text-white shadow-md transition ${
              isSyncing
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-l from-sky-500 via-indigo-500 to-blue-600 hover:from-sky-600 hover:via-indigo-600 hover:to-blue-700"
            }`}
          >
            <RotateCw size={18} className={isSyncing ? "animate-spin" : ""} />
            {isSyncing ? "در حال همگام‌سازی..." : "شروع همگام‌سازی پروفرماها"}
          </button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="text-emerald-600" size={18} />
            <h3 className="text-xs sm:text-sm font-semibold text-slate-800">
              وضعیت فعلی همگام‌سازی
            </h3>
          </div>

          {!taskId ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              تاکنون همگام‌سازی فعالی شروع نشده است.
            </p>
          ) : (
            <div className="space-y-2 text-xs text-slate-700">
              <div className="flex items-center justify-between gap-2">
                <span>شناسه تسک:</span>
                <code className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 truncate">
                  {taskId}
                </code>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>وضعیت:</span>
                <span
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                    taskState === "SUCCESS"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : taskState === "FAILURE" || taskState === "REVOKED"
                      ? "bg-rose-50 text-rose-700 border border-rose-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                >
                  {taskState || "در انتظار..."}
                </span>
              </div>
            </div>
          )}

          {summary && (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <h4 className="text-[11px] font-semibold text-slate-600 mb-1.5">
                خلاصه آخرین همگام‌سازی:
              </h4>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                <div className="flex justify-between gap-2">
                  <dt>تعداد کل دریافتی:</dt>
                  <dd>{formatNumber(summary.fetched)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>وضعیت «ثبت سفارش»:</dt>
                  <dd>{formatNumber(summary["eligible_status_ثبت_سفارش"])}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>ایجاد جدید:</dt>
                  <dd>{formatNumber(summary.created)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>بروزرسانی:</dt>
                  <dd>{formatNumber(summary.updated)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>رکوردهای ناقص:</dt>
                  <dd>{formatNumber(summary.skipped_missing_keys)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>خطای API جزئیات:</dt>
                  <dd>{formatNumber(summary.detail_api_errors)}</dd>
                </div>
              </dl>
              {summary.timestamp && (
                <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
                  <Clock size={12} />
                  <span>زمان ثبت: {summary.timestamp}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* کارت ۲: دریافت پیش‌کوتاژها */}
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-7 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white shadow-sm">
            <ClipboardList size={20} />
          </div>
          <div className="flex flex-col items-end">
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              دریافت پیش‌کوتاژها از سامانه جامع
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500">
              خواندن و ذخیره پیش‌کوتاژها در دیتابیس سیستم
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
          اتصال به سامانه بر اساس آخرین کوکی‌های ثبت‌شده انجام می‌شود. می‌توانید
          فقط تست اتصال انجام دهید یا نتایج را در دیتابیس ذخیره کنید و حالت
          Debug را برای بررسی دقیق‌تر فعال نمایید.
        </p>

        <div className="space-y-4 text-xs sm:text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                اندازه صفحه (pageSize)
              </label>
              <input
                type="number"
                min="1"
                value={pcPageSize}
                onChange={(e) => setPcPageSize(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                شروع از ردیف (startIndex)
              </label>
              <input
                type="number"
                min="0"
                value={pcStartIndex}
                onChange={(e) => setPcStartIndex(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={pcDoSave}
                onChange={(e) => setPcDoSave(e.target.checked)}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              ذخیره در دیتابیس (در حالت خاموش فقط تست اتصال انجام می‌شود)
            </label>
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={pcDebug}
                onChange={(e) => setPcDebug(e.target.checked)}
                className="w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
              />
              فعال‌سازی اطلاعات Debug
            </label>
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={pcEchoUpstream}
                onChange={(e) => setPcEchoUpstream(e.target.checked)}
                className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              ضمیمه کردن پاسخ خام NTSW (upstream)
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              حداکثر تعداد صفحات بازگشتی برای Debug
            </label>
            <input
              type="number"
              min="1"
              value={pcMaxPages}
              onChange={(e) => setPcMaxPages(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={startPreCottageFetch}
            disabled={pcIsRunning}
            className={`px-4 sm:px-5 py-2.5 inline-flex items-center gap-2 rounded-xl text-xs sm:text-sm font-semibold text-white shadow-md transition ${
              pcIsRunning
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-l from-indigo-500 via-purple-500 to-violet-600 hover:from-indigo-600 hover:via-purple-600 hover:to-violet-700"
            }`}
          >
            <RotateCw size={18} className={pcIsRunning ? "animate-spin" : ""} />
            {pcIsRunning
              ? "در حال دریافت پیش‌کوتاژ..."
              : "شروع دریافت پیش‌کوتاژها"}
          </button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="text-indigo-600" size={18} />
            <h3 className="text-xs sm:text-sm font-semibold text-slate-800">
              وضعیت فعلی دریافت پیش‌کوتاژ
            </h3>
          </div>

          {!pcTaskId ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              تاکنون تسک فعالی برای دریافت پیش‌کوتاژ شروع نشده است.
            </p>
          ) : (
            <div className="space-y-2 text-xs text-slate-700">
              <div className="flex items-center justify-between gap-2">
                <span>شناسه تسک:</span>
                <code className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 truncate">
                  {pcTaskId}
                </code>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>وضعیت:</span>
                <span
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                    pcTaskState === "SUCCESS"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : pcTaskState === "FAILURE" || pcTaskState === "REVOKED"
                      ? "bg-rose-50 text-rose-700 border border-rose-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                >
                  {pcTaskState || "در انتظار..."}
                </span>
              </div>
            </div>
          )}

          {pcSummary && (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <h4 className="text-[11px] font-semibold text-slate-600 mb-1.5">
                خلاصه آخرین دریافت پیش‌کوتاژ:
              </h4>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                <div className="flex justify-between gap-2">
                  <dt>تعداد کل دریافتی:</dt>
                  <dd>{formatNumber(pcSummary.fetched_count)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>تعداد ذخیره:</dt>
                  <dd>{formatNumber(pcSummary.saved_count)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>تعداد بروزرسانی:</dt>
                  <dd>{formatNumber(pcSummary.updated_count)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>رکوردهای رد شده:</dt>
                  <dd>{formatNumber(pcSummary.skipped?.length || 0)}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* history for pre-cottage in this session */}
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="text-purple-600" size={18} />
            <h3 className="text-xs sm:text-sm font-semibold text-slate-800">
              سوابق دریافت پیش‌کوتاژ در این نشست
            </h3>
          </div>

          {pcHistory.length === 0 ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              هنوز دریافت پیش‌کوتاژی در این نشست انجام نشده است.
            </p>
          ) : (
            <ul className="space-y-2 max-h-56 overflow-y-auto text-[11px] sm:text-xs text-slate-700">
              {pcHistory.map((job) => (
                <li
                  key={job.id}
                  className="bg-white border border-slate-200 rounded-xl px-3 py-2 space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900">
                      تسک: {job.id.slice(0, 8)}...
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                        job.state === "SUCCESS"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : job.state === "FAILURE" || job.state === "REVOKED"
                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      {job.state}
                    </span>
                  </div>
                  {job.summary && (
                    <div className="flex items-center justify-between gap-2">
                      <span>ذخیره / بروزرسانی:</span>
                      <span>
                        {formatNumber(job.summary.saved_count)} /{" "}
                        {formatNumber(job.summary.updated_count)}
                      </span>
                    </div>
                  )}
                  {job.finishedAt && (
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-1">
                      <Clock size={11} />
                      <span>{job.finishedAt}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* کارت ۳: ایمپورت کوتاژهای سبز از سامانه جامع */}
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-7 space-y-5 xl:col-span-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-sm">
            <ClipboardList size={20} />
          </div>
          <div className="flex flex-col items-end">
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              ایمپورت کوتاژهای گمرکی (Green Declaration) از سامانه جامع
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500">
              همگام‌سازی کوتاژها با مدل‌های Cottage و CottageGoods
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
          مقادیر <span className="font-mono text-[11px]">ssdsshGUID</span> و{" "}
          <span className="font-mono text-[11px]">urlVCodeInt</span> به‌صورت
          خودکار از مدل{" "}
          <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">
            NtswCookies
          </code>{" "}
          خوانده می‌شود. تنها اندازه صفحه و اندیس شروع را مشخص کنید.
        </p>

        <div className="space-y-4 text-xs sm:text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                اندازه صفحه (PageSize)
              </label>
              <input
                type="number"
                min="1"
                value={ciPageSize}
                onChange={(e) => setCiPageSize(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                شروع از ردیف (StartIndex)
              </label>
              <input
                type="number"
                min="0"
                value={ciStartIndex}
                onChange={(e) => setCiStartIndex(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                حداکثر صفحات
              </label>
              <input
                type="number"
                min="1"
                value={ciMaxPages}
                onChange={(e) => setCiMaxPages(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={startCottageImport}
            disabled={ciIsRunning}
            className={`px-4 sm:px-5 py-2.5 inline-flex items-center gap-2 rounded-xl text-xs sm:text-sm font-semibold text-white shadow-md transition ${
              ciIsRunning
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-l from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-600 hover:via-teal-600 hover:to-emerald-700"
            }`}
          >
            <RotateCw size={18} className={ciIsRunning ? "animate-spin" : ""} />
            {ciIsRunning ? "در حال ایمپورت کوتاژها..." : "شروع ایمپورت کوتاژها"}
          </button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="text-emerald-600" size={18} />
            <h3 className="text-xs sm:text-sm font-semibold text-slate-800">
              وضعیت فعلی ایمپورت کوتاژها
            </h3>
          </div>

          {!ciTaskId ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              تاکنون تسک ایمپورتی برای کوتاژها شروع نشده است.
            </p>
          ) : (
            <div className="space-y-2 text-xs text-slate-700">
              <div className="flex items-center justify-between gap-2">
                <span>شناسه تسک:</span>
                <code className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 truncate">
                  {ciTaskId}
                </code>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span>وضعیت:</span>
                <span
                  className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                    ciTaskState === "SUCCESS"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : ciTaskState === "FAILURE" || ciTaskState === "REVOKED"
                      ? "bg-rose-50 text-rose-700 border border-rose-200"
                      : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                >
                  {ciTaskState || "در انتظار..."}
                </span>
              </div>
            </div>
          )}

          {ciSummary && (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <h4 className="text-[11px] font-semibold text-slate-600 mb-1.5">
                خلاصه آخرین ایمپورت کوتاژ:
              </h4>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                <div className="flex justify_between gap-2">
                  <dt>تعداد کل دریافت‌شده:</dt>
                  <dd>{formatNumber(ciSummary.fetched)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>کوتاژهای ایجادشده:</dt>
                  <dd>{formatNumber(ciSummary.created)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>کوتاژهای بروزرسانی‌شده:</dt>
                  <dd>{formatNumber(ciSummary.updated)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>رکوردهای دارای خطا:</dt>
                  <dd>{formatNumber(ciSummary.errors?.length || 0)}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* history for cottage import in this session */}
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="text-teal-600" size={18} />
            <h3 className="text-xs sm:text-sm font-semibold text-slate-800">
              سوابق ایمپورت کوتاژ در این نشست
            </h3>
          </div>

          {ciHistory.length === 0 ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              هنوز ایمپورت کوتاژی در این نشست انجام نشده است.
            </p>
          ) : (
            <ul className="space-y-2 max-h-60 overflow-y-auto text-[11px] sm:text-xs text-slate-700">
              {ciHistory.map((job) => (
                <li
                  key={job.id}
                  className="bg-white border border-slate-200 rounded-xl px-3 py-2 space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900">
                      تسک: {job.id.slice(0, 8)}...
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                        job.state === "SUCCESS"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : job.state === "FAILURE" || job.state === "REVOKED"
                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      {job.state}
                    </span>
                  </div>
                  {job.summary && (
                    <div className="flex items-center justify-between gap-2">
                      <span>ایجاد / بروزرسانی:</span>
                      <span>
                        {formatNumber(job.summary.created)} /{" "}
                        {formatNumber(job.summary.updated)}
                      </span>
                    </div>
                  )}
                  {job.finishedAt && (
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-1">
                      <Clock size={11} />
                      <span>{job.finishedAt}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminJobsSection;
